// Main.tsx
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link,
  Typography,
} from "@mui/material";
import { Home as HomeIcon, NoteAdd as NoteAddIcon } from "@mui/icons-material";

import FileGrid, { encodeKey, FileItem, isDirectory } from "./FileGrid";
import { ViewMode, SortBy } from "./App";
import FilePreviewDrawer from "./FilePreviewDrawer";
import MultiSelectToolbar from "./MultiSelectToolbar";
import UploadDrawer, { UploadFab } from "./UploadDrawer";
import TextPadDrawer from "./TextPadDrawer";
import { copyPaste, fetchPath } from "./app/transfer";
import { useTransferQueue, useUploadEnqueue } from "./app/transferQueue";

// URL state helpers
function parseUrlState(): { path: string; preview: string | null } {
  const rawHash = window.location.hash.slice(1);
  if (!rawHash) return { path: "", preview: null };
  
  const hash = rawHash
    .split("/")
    .map((segment) => decodeURIComponent(segment))
    .join("/");
  
  if (hash.endsWith("/")) {
    return { path: hash, preview: null };
  } else {
    const lastSlash = hash.lastIndexOf("/");
    const path = lastSlash >= 0 ? hash.slice(0, lastSlash + 1) : "";
    return { path, preview: hash };
  }
}

function updateUrlState(path: string, preview: string | null) {
  const value = preview || path;
  const encoded = value
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  const newUrl = encoded ? `#${encoded}` : window.location.pathname;
  window.history.pushState(null, "", newUrl);
}

// Centered helper
function Centered({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      {children}
    </Box>
  );
}

// Breadcrumb component
function PathBreadcrumb({
  path,
  onCwdChange,
}: {
  path: string;
  onCwdChange: (newCwd: string) => void;
}) {
  const parts = path.replace(/\/$/, "").split("/");

  return (
    <Breadcrumbs separator="›" sx={{ padding: 1 }}>
      <Button onClick={() => onCwdChange("")} sx={{ minWidth: 0, padding: 0 }}>
        <HomeIcon />
      </Button>
      {parts.map((part, index) =>
        index === parts.length - 1 ? (
          <Typography key={index} color="text.primary">
            {part}
          </Typography>
        ) : (
          <Link
            key={index}
            component="button"
            onClick={() => {
              onCwdChange(parts.slice(0, index + 1).join("/") + "/");
            }}
          >
            {part}
          </Link>
        )
      )}
    </Breadcrumbs>
  );
}

// DropZone wrapper
function DropZone({
  children,
  onDrop,
}: {
  children: React.ReactNode;
  onDrop: (files: FileList) => void;
}) {
  const [dragging, setDragging] = useState(false);

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflowY: "auto",
        backgroundColor: (theme) => theme.palette.background.default,
        filter: dragging ? "brightness(0.9)" : "none",
        transition: "filter 0.2s",
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(e.dataTransfer.files);
        setDragging(false);
      }}
    >
      {children}
    </Box>
  );
}

// Main Component
function Main({
  search,
  onError,
  viewMode,
  sortBy,
}: {
  search: string;
  onError: (error: Error) => void;
  viewMode: ViewMode;
  sortBy: SortBy;
}) {
  const initialState = parseUrlState();
  const [cwd, setCwdState] = useState(initialState.path);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [multiSelected, setMultiSelected] = useState<string[] | null>(null);
  const [showUploadDrawer, setShowUploadDrawer] = useState(false);
  const [showTextPadDrawer, setShowTextPadDrawer] = useState(false);
  const [previewFile, setPreviewFileState] = useState<FileItem | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(initialState.preview);
  const [editFile, setEditFile] = useState<FileItem | null>(null);
  const [lastUploadKey, setLastUploadKey] = useState<string | null>(null);
  const skipUrlUpdate = useRef(false);

  const transferQueue = useTransferQueue();
  const uploadEnqueue = useUploadEnqueue();

  const setCwd = useCallback((newCwd: string) => {
    setCwdState(newCwd);
    setPreviewFileState(null);
    setPreviewKey(null);
    if (!skipUrlUpdate.current) {
      updateUrlState(newCwd, null);
    }
  }, []);

  const setPreviewFile = useCallback((file: FileItem | null) => {
    setPreviewFileState(file);
    setPreviewKey(file?.key || null);
    if (!skipUrlUpdate.current) {
      updateUrlState(cwd, file?.key || null);
    }
  }, [cwd]);

  useEffect(() => {
    const handlePopState = () => {
      skipUrlUpdate.current = true;
      const state = parseUrlState();
      setCwdState(state.path);
      setPreviewKey(state.preview);
      setPreviewFileState(null);
      skipUrlUpdate.current = false;
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (previewKey && files.length > 0) {
      const file = files.find((f) => f.key === previewKey);
      if (file) setPreviewFileState(file);
    }
  }, [previewKey, files]);

  const fetchFiles = useCallback(() => {
    fetchPath(cwd)
      .then((files) => {
        setFiles(files);
        setMultiSelected(null);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  }, [cwd, onError]);

  useEffect(() => setLoading(true), [cwd]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (!transferQueue.length) return;
    const lastFile = transferQueue[transferQueue.length - 1];
    if (["pending", "in-progress"].includes(lastFile.status)) {
      setLastUploadKey(lastFile.remoteKey);
    } else if (lastUploadKey) {
      fetchFiles();
      setLastUploadKey(null);
    }
  }, [cwd, fetchFiles, lastUploadKey, transferQueue]);

  const filteredFiles = useMemo(() => {
    const filtered = search
      ? files.filter((file) =>
          file.key.toLowerCase().includes(search.toLowerCase())
        )
      : files;

    return [...filtered].sort((a, b) => {
      // Directories always first
      if (isDirectory(a) && !isDirectory(b)) return -1;
      if (!isDirectory(a) && isDirectory(b)) return 1;

      // Then apply user-selected sort
      switch (sortBy) {
        case "name":
          return a.key.localeCompare(b.key);
        case "date":
          return new Date(b.uploaded).getTime() - new Date(a.uploaded).getTime();
        case "size":
          return b.size - a.size;
        default:
          return 0;
      }
    });
  }, [files, search, sortBy]);

  const handleMultiSelect = useCallback((key: string) => {
    setMultiSelected((prev) => {
      if (prev === null) return [key];
      if (prev.includes(key)) {
        const updated = prev.filter((k) => k !== key);
        return updated.length ? updated : null;
      }
      return [...prev, key];
    });
  }, []);

  const handleEditFile = useCallback((file: FileItem) => {
    setEditFile(file);
    setPreviewFile(null);
    setShowTextPadDrawer(true);
  }, [setPreviewFile]);

  const handleTextPadClose = useCallback((open: boolean) => {
    setShowTextPadDrawer(open);
    if (!open) {
      setEditFile(null); // Clear edit file when closing
    }
  }, []);

  return (
    <>
      {cwd && <PathBreadcrumb path={cwd} onCwdChange={setCwd} />}

      {loading ? (
        <Centered>
          <CircularProgress />
        </Centered>
      ) : (
        <DropZone
          onDrop={(files) => {
            uploadEnqueue(
              ...Array.from(files).map((file) => ({ file, basedir: cwd }))
            );
          }}
        >
          <FileGrid
            files={filteredFiles}
            onCwdChange={(newCwd: string) => setCwd(newCwd)}
            onFileClick={(file) => setPreviewFile(file)}
            multiSelected={multiSelected}
            onMultiSelect={handleMultiSelect}
            emptyMessage={<Centered>No files or folders</Centered>}
            viewMode={viewMode}
          />
        </DropZone>
      )}

      {multiSelected === null && (
        <>
          <UploadFab onClick={() => setShowUploadDrawer(true)} />
          <Button
            variant="contained"
            startIcon={<NoteAddIcon />}
            sx={{
              position: "fixed",
              bottom: 90,
              right: 24,
              zIndex: 999,
            }}
            onClick={() => setShowTextPadDrawer(true)}
          >
            Open TextPad
          </Button>
        </>
      )}

      <UploadDrawer
        open={showUploadDrawer}
        setOpen={setShowUploadDrawer}
        cwd={cwd}
        onUpload={fetchFiles}
      />

      <TextPadDrawer
        open={showTextPadDrawer}
        setOpen={handleTextPadClose}
        cwd={cwd}
        onUpload={fetchFiles}
        editFile={editFile}
      />

      <FilePreviewDrawer
        file={previewFile}
        onClose={() => setPreviewFile(null)}
        onEdit={handleEditFile}
      />

      <MultiSelectToolbar
        multiSelected={multiSelected}
        onClose={() => setMultiSelected(null)}
        onDownload={() => {
          if (multiSelected?.length !== 1) return;
          const a = document.createElement("a");
          a.href = `/webdav/${encodeKey(multiSelected[0])}`;
          a.download = multiSelected[0].split("/").pop()!;
          a.click();
        }}
        onRename={async () => {
          if (multiSelected?.length !== 1) return;
          const newName = window.prompt("Rename to:");
          if (!newName) return;
          await copyPaste(multiSelected[0], cwd + newName, true);
          fetchFiles();
        }}
        onDelete={async () => {
          if (!multiSelected?.length) return;
          const filenames = multiSelected
            .map((key) => key.replace(/\/$/, "").split("/").pop())
            .join("\n");
          const confirmMessage = "Delete the following file(s) permanently?";
          if (!window.confirm(`${confirmMessage}\n${filenames}`)) return;
          for (const key of multiSelected)
            await fetch(`/webdav/${encodeKey(key)}`, { method: "DELETE" });
          fetchFiles();
        }}
        onShare={() => {
          if (multiSelected?.length !== 1) return;
          const url = new URL(
            `/webdav/${encodeKey(multiSelected[0])}`,
            window.location.href
          );
          navigator.share({ url: url.toString() });
        }}
      />
    </>
  );
}

export default Main;
